import React, { useEffect, useId, useState } from "react";

import { Btn, Drawer, Field, TextInput } from "../catalogProductStudio/edit/StudioUi";
import {
  DESCRIPTION_MAX_LENGTH,
  NAME_MAX_LENGTH,
  getApiErrorCode,
  humanizeMetaAdsError,
} from "./metaAdsModel";

/**
 * Name + optional description, for creating or editing a product set.
 * `onSubmit` returns a promise; a duplicate name is shown on the field itself,
 * anything else as a message above the actions. The drawer never closes on
 * failure, so nothing typed is lost.
 */
const ProductSetDetailsDrawer = ({ isOpen, mode = "create", initialValues, onClose, onSubmit, tt }) => {
  const nameId = useId();
  const descriptionId = useId();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [nameError, setNameError] = useState("");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(initialValues?.name || "");
    setDescription(initialValues?.description || "");
    setNameError("");
    setFormError("");
    setIsSubmitting(false);
  }, [isOpen, initialValues]);

  const trimmedName = name.trim();
  const isEdit = mode === "edit";
  const unchanged =
    isEdit &&
    trimmedName === (initialValues?.name || "").trim() &&
    description.trim() === (initialValues?.description || "").trim();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    if (trimmedName.length < 2) {
      setNameError(tt("nameTooShort"));
      return;
    }
    setIsSubmitting(true);
    setFormError("");
    try {
      await onSubmit({ name: trimmedName, description: description.trim() || null });
    } catch (error) {
      if (getApiErrorCode(error) === "PRODUCT_SET_NAME_TAKEN") {
        setNameError(tt("errNameTaken"));
      } else {
        setFormError(humanizeMetaAdsError(error, tt, isEdit ? "errUpdate" : "errCreate"));
      }
      setIsSubmitting(false);
    }
  };

  const formId = `${nameId}-form`;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={isSubmitting ? () => {} : onClose}
      size="sm"
      icon="layers"
      title={isEdit ? tt("editTitle") : tt("createTitle")}
      description={isEdit ? tt("editDescription") : tt("createDescription")}
      closeLabel={tt("close")}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {tt("cancel")}
          </Btn>
          <Btn
            variant="primary"
            type="submit"
            form={formId}
            icon={isEdit ? "check" : null}
            iconRight={isEdit ? null : "arrowRight"}
            disabled={isSubmitting || !trimmedName || unchanged}
          >
            {isSubmitting ? tt("saving") : isEdit ? tt("saveDetails") : tt("createAndChoose")}
          </Btn>
        </>
      }
    >
      <form id={formId} className="mka-form" onSubmit={handleSubmit} noValidate>
        <Field
          label={tt("nameLabel")}
          htmlFor={nameId}
          error={nameError}
          hint={isEdit ? tt("nameHintEdit") : tt("nameHint")}
          counter={`${name.length}/${NAME_MAX_LENGTH}`}
        >
          <TextInput
            id={nameId}
            value={name}
            maxLength={NAME_MAX_LENGTH}
            placeholder={tt("namePlaceholder")}
            autoComplete="off"
            data-autofocus
            aria-invalid={Boolean(nameError)}
            onChange={(value) => {
              setName(value);
              if (nameError) setNameError("");
            }}
          />
        </Field>
        <Field
          label={tt("descriptionLabel")}
          htmlFor={descriptionId}
          optional
          optionalLabel={tt("optional")}
          hint={tt("descriptionHint")}
          counter={`${description.length}/${DESCRIPTION_MAX_LENGTH}`}
        >
          <TextInput
            id={descriptionId}
            multiline
            rows={3}
            value={description}
            maxLength={DESCRIPTION_MAX_LENGTH}
            placeholder={tt("descriptionPlaceholder")}
            onChange={setDescription}
          />
        </Field>
        {formError ? (
          <p className="mka-form__error" role="alert">
            {formError}
          </p>
        ) : null}
      </form>
    </Drawer>
  );
};

export default ProductSetDetailsDrawer;
